import { useMemo, useRef } from "react";
import * as THREE from "three";
import { useFrame, useThree } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import { useSim } from "../lib/store";
import { soilMultiplier, fractureFactor } from "../lib/seismic";
import { sunParams } from "../lib/lighting";
import { Skyscraper } from "./street/Buildings";
import { Metropolis } from "./street/Metropolis";
import { CrowdAI } from "./street/CrowdAI";
import { Debris } from "./street/Debris";
import { WaterPlane } from "./street/WaterPlane";

const ROADS = [-42, -28, -14, 0, 14, 28, 42];
const CAM_HOME = new THREE.Vector3(62, 44, 74);
const FLY_DUR = 1.8;
const HERO = [
  { x: -7, z: -7, h: 22, seed: 11 }, { x: 7, z: 7, h: 30, seed: 22 },
  { x: 7, z: -7, h: 18, seed: 33 }, { x: -7, z: 7, h: 26, seed: 44 },
  { x: -14, z: 4, h: 16, seed: 55 }, { x: 14, z: -4, h: 20, seed: 66 },
];

function runningMaxMag(st) {
  const elapsed = (performance.now() - st.simStart) / 1000;
  let m = 0;
  if (st.simulating) for (const e of st.events) if (elapsed <= e.duration + 0.5) m = Math.max(m, e.magnitude);
  return m || st.magnitude;
}

function GroundCracks() {
  const magnitude = useSim((s) => s.magnitude);
  const duration = useSim((s) => s.duration);
  const matRef = useRef();
  const maxLenRef = useRef(1);

  const geom = useMemo(() => {
    const f = fractureFactor(duration);
    const magF = THREE.MathUtils.clamp((magnitude - 5.5) / 4, 0, 1);
    const branches = Math.round(24 + f * 70 + magF * 40);
    const maxLen = 24 + f * 60 + magF * 30;
    maxLenRef.current = maxLen;
    const pos = [];
    const dist = [];
    for (let b = 0; b < branches; b++) {
      let a = (b / branches) * Math.PI * 2 + Math.random() * 0.4;
      let x = 0, z = 0, d = 0;
      const steps = 14;
      const len = maxLen * (0.4 + Math.random() * 0.6);
      for (let i = 0; i < steps; i++) {
        a += (Math.random() - 0.5) * 0.5;
        const seg = len / steps;
        const nx = x + Math.cos(a) * seg;
        const nz = z + Math.sin(a) * seg;
        const nd = d + seg;
        pos.push(x, 0, z, nx, 0, nz);
        dist.push(d, nd);
        x = nx; z = nz; d = nd;
      }
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
    g.setAttribute("aDist", new THREE.Float32BufferAttribute(dist, 1));
    return g;
  }, [magnitude, duration]);

  const uniforms = useMemo(() => ({ uFront: { value: 0 }, uIntensity: { value: 1 } }), []);
  useFrame(() => {
    const st = useSim.getState();
    const magF = THREE.MathUtils.clamp((st.magnitude - 5) / 4.5, 0, 1);
    const targetFront = st.simulating ? maxLenRef.current * (0.08 + 0.95 * st.simProgress) : 0;
    uniforms.uFront.value += (targetFront - uniforms.uFront.value) * 0.12;
    uniforms.uIntensity.value = 0.6 + magF * 1.4;
  });

  return (
    <lineSegments geometry={geom} position={[0, 0.03, 0]}>
      <shaderMaterial
        ref={matRef}
        uniforms={uniforms}
        transparent
        depthWrite={false}
        blending={THREE.AdditiveBlending}
        vertexShader={`
          attribute float aDist; varying float vD;
          void main(){ vD = aDist; gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }
        `}
        fragmentShader={`
          uniform float uFront; uniform float uIntensity; varying float vD;
          void main(){
            float show = 1.0 - smoothstep(uFront - 3.0, uFront, vD);
            float edge = exp(-pow((vD - uFront)/2.5, 2.0));
            vec3 col = mix(vec3(0.7,0.04,0.03), vec3(1.0,0.4,0.15), edge);
            float a = clamp(show*0.7 + edge, 0.0, 1.0) * uIntensity;
            if(a < 0.02) discard;
            gl_FragColor = vec4(col, a);
          }
        `}
      />
    </lineSegments>
  );
}

// Sweeping emergency searchlights that rake the ruined skyline during the collapse aftermath.
function Searchlights() {
  const refs = useRef([]);
  const beams = useMemo(
    () => [
      { x: -30, z: -30, col: "#cfe6ff", speed: 0.5, tilt: 0.75 },
      { x: 32, z: 22, col: "#ffd9b0", speed: -0.42, tilt: 0.62 },
      { x: 8, z: -34, col: "#dbeeff", speed: 0.66, tilt: 0.82 },
    ],
    []
  );
  useFrame((state) => {
    const st = useSim.getState();
    const elapsed = (performance.now() - st.simStart) / 1000;
    let mag = 0;
    if (st.simulating) for (const e of st.events) if (elapsed <= e.duration + 0.5) mag = Math.max(mag, e.magnitude);
    if (st.simulating && mag >= 7.5 && st.simProgress > 0.35) window.__disasterUntil = performance.now() + 10000;
    const active = performance.now() < (window.__disasterUntil || 0);
    const t = state.clock.elapsedTime;
    beams.forEach((b, i) => {
      const grp = refs.current[i];
      if (!grp) return;
      grp.visible = active;
      if (active) {
        grp.rotation.y = t * b.speed + i * 2.1;
        const flick = 0.85 + Math.abs(Math.sin(t * 9 + i)) * 0.15;
        const beam = grp.children[0]?.children[0];
        if (beam?.material) beam.material.opacity = 0.12 * flick;
      }
    });
  });
  return (
    <group>
      {beams.map((b, i) => (
        <group key={i} ref={(el) => (refs.current[i] = el)} position={[b.x, 15, b.z]} visible={false}>
          <group rotation={[b.tilt, 0, 0]}>
            <mesh position={[0, -19, 0]}>
              <coneGeometry args={[7, 38, 24, 1, true]} />
              <meshBasicMaterial color={b.col} transparent opacity={0.12} side={THREE.DoubleSide} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
            </mesh>
            <pointLight position={[0, -30, 0]} color={b.col} intensity={2.4} distance={40} decay={2} />
          </group>
        </group>
      ))}
    </group>
  );
}

// Circling rescue helicopters with downward searchlight cones over the collapse zone.
function RescueChoppers() {
  const choppers = useMemo(
    () => [
      { r: 26, y: 27, speed: 0.35, phase: 0, col: "#101820" },
      { r: 35, y: 33, speed: -0.26, phase: Math.PI, col: "#14180f" },
    ],
    []
  );
  const grpRefs = useRef([]);
  const rotorRefs = useRef([]);
  const tailRotorRefs = useRef([]);
  const navRefs = useRef([]);
  const beamRefs = useRef([]);

  useFrame((state) => {
    const active = performance.now() < (window.__disasterUntil || 0);
    const t = state.clock.elapsedTime;
    choppers.forEach((c, i) => {
      const g = grpRefs.current[i];
      if (!g) return;
      g.visible = active;
      if (!active) return;
      const a = t * c.speed + c.phase;
      g.position.set(Math.cos(a) * c.r, c.y + Math.sin(t * 0.8 + i) * 0.6, Math.sin(a) * c.r);
      g.rotation.y = -a; // nose points along the orbit tangent
      g.rotation.z = 0.16; // bank into the turn
      if (rotorRefs.current[i]) rotorRefs.current[i].rotation.y = t * 42;
      if (tailRotorRefs.current[i]) tailRotorRefs.current[i].rotation.x = t * 58;
      if (navRefs.current[i]) navRefs.current[i].material.emissiveIntensity = (Math.sin(t * 6 + i) > 0 ? 1 : 0.1) * 3;
      if (beamRefs.current[i]) beamRefs.current[i].material.opacity = 0.1 + Math.abs(Math.sin(t * 3 + i)) * 0.06;
    });
  });

  return (
    <group>
      {choppers.map((c, i) => (
        <group key={i} ref={(el) => (grpRefs.current[i] = el)} visible={false}>
          {/* fuselage */}
          <mesh><boxGeometry args={[1.1, 0.9, 2.2]} /><meshStandardMaterial color={c.col} metalness={0.5} roughness={0.5} /></mesh>
          {/* cockpit glass */}
          <mesh position={[0, 0.1, 1.15]}><sphereGeometry args={[0.55, 12, 12]} /><meshStandardMaterial color="#3a5a7a" metalness={0.3} roughness={0.2} emissive="#0a1a2a" emissiveIntensity={0.6} /></mesh>
          {/* tail boom + fin */}
          <mesh position={[0, 0.15, -2.0]}><boxGeometry args={[0.3, 0.3, 2.4]} /><meshStandardMaterial color={c.col} metalness={0.4} roughness={0.6} /></mesh>
          <mesh position={[0, 0.5, -3.0]}><boxGeometry args={[0.1, 0.7, 0.5]} /><meshStandardMaterial color={c.col} /></mesh>
          {/* landing skids */}
          <mesh position={[0, -0.6, 0]}><boxGeometry args={[1.3, 0.06, 1.6]} /><meshStandardMaterial color="#1a1a1a" /></mesh>
          {/* main rotor (spins) */}
          <group ref={(el) => (rotorRefs.current[i] = el)} position={[0, 0.66, 0.2]}>
            <mesh><boxGeometry args={[7, 0.04, 0.28]} /><meshStandardMaterial color="#0a0a0a" transparent opacity={0.5} /></mesh>
            <mesh rotation={[0, Math.PI / 2, 0]}><boxGeometry args={[7, 0.04, 0.28]} /><meshStandardMaterial color="#0a0a0a" transparent opacity={0.5} /></mesh>
            <mesh position={[0, 0.05, 0]}><cylinderGeometry args={[0.12, 0.12, 0.2, 8]} /><meshStandardMaterial color="#333" /></mesh>
          </group>
          {/* tail rotor (spins) */}
          <group ref={(el) => (tailRotorRefs.current[i] = el)} position={[0.2, 0.15, -3.1]}>
            <mesh><boxGeometry args={[0.06, 1.4, 0.12]} /><meshStandardMaterial color="#0a0a0a" transparent opacity={0.5} /></mesh>
          </group>
          {/* blinking red nav beacon */}
          <mesh ref={(el) => (navRefs.current[i] = el)} position={[0, -0.5, 0]}><sphereGeometry args={[0.12, 8, 8]} /><meshStandardMaterial color="#ff2a2a" emissive="#ff0000" emissiveIntensity={3} toneMapped={false} /></mesh>
          {/* downward searchlight cone (apex at chopper, base near ground) */}
          <mesh ref={(el) => (beamRefs.current[i] = el)} position={[0, -c.y / 2, 0.4]}>
            <coneGeometry args={[c.y * 0.34, c.y, 20, 1, true]} />
            <meshBasicMaterial color="#fff3d0" transparent opacity={0.12} side={THREE.DoubleSide} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
          </mesh>
          <pointLight position={[0, -2, 0.4]} color="#fff3d0" intensity={3} distance={44} decay={2} />
        </group>
      ))}
    </group>
  );
}

function Roads() {
  return (
    <group>
      {ROADS.map((c) => (
        <group key={`r${c}`}>
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[c, 0.011, 0]} receiveShadow>
            <planeGeometry args={[6, 130]} />
            <meshStandardMaterial color="#0a0d15" roughness={0.95} metalness={0.1} />
          </mesh>
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.011, c]} receiveShadow>
            <planeGeometry args={[130, 6]} />
            <meshStandardMaterial color="#0a0d15" roughness={0.95} metalness={0.1} />
          </mesh>
        </group>
      ))}
    </group>
  );
}

export function StreetSandbox() {
  const { camera } = useThree();
  const ambientRef = useRef();
  const sunRef = useRef();
  const hemiRef = useRef();
  const redRef = useRef();
  const neon1Ref = useRef();
  const neon2Ref = useRef();
  const backMatRef = useRef();
  const groundMatRef = useRef();
  const controlsRef = useRef();
  const lampRefs = useRef([]);
  const flyRef = useRef(null);
  const lastNonceRef = useRef(-1);

  const dayCol = useMemo(() => new THREE.Color("#8fb4dd"), []);
  const nightCol = useMemo(() => new THREE.Color("#05070d"), []);
  const groundDay = useMemo(() => new THREE.Color("#3a4048"), []);
  const groundNight = useMemo(() => new THREE.Color("#0c1018"), []);
  const lamps = useMemo(() => {
    const arr = [];
    for (const c of ROADS) for (const s of [-30, 0, 30]) { arr.push([c + 3.4, s]); arr.push([s, c + 3.4]); }
    return arr;
  }, []);

  useMemo(() => {
    camera.position.copy(CAM_HOME);
    camera.lookAt(0, 3, 0);
  }, [camera]);

  useFrame(() => {
    const st = useSim.getState();

    if (st.focusNonce !== lastNonceRef.current) {
      lastNonceRef.current = st.focusNonce;
      if (st.focusNonce > 0) flyRef.current = { t0: performance.now(), from: new THREE.Vector3(95, 70, 100) };
    }
    if (flyRef.current) {
      if (controlsRef.current) controlsRef.current.enabled = false;
      const t = (performance.now() - flyRef.current.t0) / 1000;
      const p = Math.min(t / FLY_DUR, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      camera.position.lerpVectors(flyRef.current.from, CAM_HOME, ease);
      camera.lookAt(0, 3, 0);
      if (p >= 1) {
        flyRef.current = null;
        if (controlsRef.current) { controlsRef.current.enabled = true; controlsRef.current.target.set(0, 3, 0); }
      }
    }

    const soil = soilMultiplier(st.soilType);
    const mag = runningMaxMag(st);
    const k = Math.pow(THREE.MathUtils.clamp((mag - 3) / 6.5, 0, 1), 2.4);
    const base = (0.001 + k * 0.012) * (0.5 + soil * 0.4);
    const env = st.simulating ? Math.sin(Math.min(st.simProgress, 1) * Math.PI) * 0.7 + 0.5 : 0.22;
    window.__streetAmp = base * env;
    window.__streetLiq = st.liquefaction;

    const { dir, dayFactor, night } = sunParams(st.timeOfDay);
    window.__streetNight = night;
    if (ambientRef.current) ambientRef.current.intensity = 0.32 + dayFactor * 0.9;
    if (sunRef.current) { sunRef.current.intensity = 0.35 + dayFactor * 3.0; sunRef.current.position.set(dir[0], dir[1], dir[2]); }
    if (hemiRef.current) hemiRef.current.intensity = 0.35 + dayFactor * 0.6;
    if (redRef.current) redRef.current.intensity = night * 7 + (st.simulating ? 3 : 0);
    if (neon1Ref.current) neon1Ref.current.intensity = night * 6;
    if (neon2Ref.current) neon2Ref.current.intensity = night * 6;
    lampRefs.current.forEach((l) => { if (l) l.intensity = night * 3; });
    if (backMatRef.current) backMatRef.current.color.copy(nightCol).lerp(dayCol, dayFactor);
    if (groundMatRef.current) groundMatRef.current.color.copy(groundNight).lerp(groundDay, dayFactor);
  });

  return (
    <group>
      <mesh scale={400}>
        <sphereGeometry args={[1, 32, 32]} />
        <meshBasicMaterial ref={backMatRef} color="#8fb4dd" side={THREE.BackSide} fog={false} />
      </mesh>

      <ambientLight ref={ambientRef} intensity={1.1} />
      <directionalLight ref={sunRef} position={[40, 60, 30]} intensity={3.2} color="#fff4e0" castShadow shadow-mapSize={[1024, 1024]} shadow-camera-far={220} shadow-camera-left={-90} shadow-camera-right={90} shadow-camera-top={90} shadow-camera-bottom={-90} />
      <hemisphereLight ref={hemiRef} args={["#bcd6ff", "#6b7280", 1.0]} />
      <pointLight ref={redRef} position={[0, 20, 0]} color="#ff3b30" intensity={0} distance={150} decay={2} />
      <pointLight ref={neon1Ref} position={[24, 8, -20]} color="#00e5ff" intensity={0} distance={90} decay={2} />
      <pointLight ref={neon2Ref} position={[-24, 8, 20]} color="#ff00d0" intensity={0} distance={90} decay={2} />

      {lamps.map(([lx, lz], i) => (
        <group key={i} position={[lx, 0, lz]}>
          <mesh position={[0, 1.4, 0]}><cylinderGeometry args={[0.06, 0.08, 2.8, 6]} /><meshStandardMaterial color="#2a2f3a" /></mesh>
          <mesh position={[0, 2.9, 0]}><sphereGeometry args={[0.16, 10, 10]} /><meshStandardMaterial color="#fff3c0" emissive="#ffdd88" emissiveIntensity={2} toneMapped={false} /></mesh>
          <pointLight ref={(el) => (lampRefs.current[i] = el)} position={[0, 2.8, 0]} color="#ffdd99" intensity={0} distance={10} decay={2} />
        </group>
      ))}

      <mesh rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
        <planeGeometry args={[150, 150]} />
        <meshStandardMaterial ref={groundMatRef} color="#0c1018" metalness={0.2} roughness={0.9} />
      </mesh>

      <Roads />
      <Metropolis />
      {HERO.map((h) => <Skyscraper key={h.seed} x={h.x} z={h.z} w={2.6} d={2.6} h={h.h * 0.35} seed={h.seed} />)}
      <CrowdAI />
      <Debris />
      <GroundCracks />
      <WaterPlane />
      <Searchlights />
      <RescueChoppers />

      <OrbitControls ref={controlsRef} makeDefault target={[0, 3, 0]} enablePan minDistance={8} maxDistance={130} maxPolarAngle={Math.PI * 0.495} />
    </group>
  );
}
